'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import {
  Button,
  IconButton,
  Text,
  Badge,
  Tooltip,
} from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Copy01Icon,
  Add01Icon,
  Delete02Icon,
} from '@terra/ui-icons';
import { useState } from 'react';
import { useDecoderStore } from '../../store/decoder-store';
import { DecoderOperationSelector } from './DecoderOperationSelector';
import type { DecoderStep as DecoderStepType } from './types';

type DecoderStepProps = {
  step: DecoderStepType;
  stepNumber: number;
};

export function DecoderStep({ step, stepNumber }: DecoderStepProps) {
  const [copied, setCopied] = useState(false);
  const updateStepOperation = useDecoderStore(
    (state) => state.updateStepOperation,
  );
  const removeStep = useDecoderStore((state) => state.removeStep);
  const toggleStepCollapse = useDecoderStore(
    (state) => state.toggleStepCollapse,
  );
  const addStep = useDecoderStore((state) => state.addStep);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(step.output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleAddStep = () => {
    addStep({ operation: 'url-decode', afterStepId: step.id });
  };

  return (
    <Box w="full">
      <HStack
        gap={1}
        px={2}
        py={1}
        bg="bg.subtle"
        borderBottomWidth={step.isCollapsed ? '0' : '1px'}
        borderColor="border.muted"
        cursor="pointer"
        onClick={() => toggleStepCollapse(step.id)}
        _hover={{ bg: 'bg.muted' }}
        flexShrink={0}
      >
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={step.isCollapsed ? 'Expand step' : 'Collapse step'}
        >
          {step.isCollapsed ? (
            <ArrowRight01Icon size={14} />
          ) : (
            <ArrowDown01Icon size={14} />
          )}
        </IconButton>

        <Badge variant="fill" size="sm">
          {stepNumber}
        </Badge>

        <Box flex="1" minW="0" onClick={(e) => e.stopPropagation()}>
          <DecoderOperationSelector
            value={step.operation}
            onChange={(operation) =>
              updateStepOperation({ stepId: step.id, operation })
            }
          />
        </Box>

        {step.error ? (
          <Tooltip title={step.error}>
            <AlertCircleIcon size={14} color="var(--colors-fg-error)" />
          </Tooltip>
        ) : (
          <Badge variant="fill" size="sm">
            {step.output.length}
          </Badge>
        )}

        <Tooltip title="Remove step">
          <IconButton
            variant="ghost"
            size="xs"
            aria-label="Remove step"
            onClick={(e) => {
              e.stopPropagation();
              removeStep(step.id);
            }}
          >
            <Delete02Icon size={14} />
          </IconButton>
        </Tooltip>
      </HStack>

      {!step.isCollapsed && (
        <Box>
          <HStack
            gap={1}
            px={2}
            py={1}
            bg="bg.muted"
            borderBottomWidth="1px"
            borderColor="border.muted"
            justifyContent="space-between"
            alignItems="center"
            flexShrink={0}
          >
            <Text
              fontSize="xs"
              fontWeight="medium"
              color={step.error ? 'fg.error' : 'fg.muted'}
            >
              {step.error ? 'Error' : 'Output'}
            </Text>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label="Copy output"
                onClick={handleCopy}
              >
                {copied ? <Tick01Icon size={14} /> : <Copy01Icon size={14} />}
              </IconButton>
            </Tooltip>
          </HStack>

          <Box bg="bg.canvas" px={2} py={2} maxH="200px" overflowY="auto">
            <Text
              fontSize="xs"
              fontFamily="monospace"
              whiteSpace="pre-wrap"
              wordBreak="break-all"
              color={step.error ? 'fg.error' : 'fg.default'}
            >
              {step.output || step.input}
            </Text>
          </Box>

          <HStack
            px={2}
            py={1}
            bg="bg.muted"
            borderTopWidth="1px"
            borderColor="border.muted"
            justifyContent="flex-end"
          >
            <Button variant="ghost" size="xs" onClick={handleAddStep}>
              <Add01Icon size={14} />
              Add Step
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
}
